import { Injectable } from '@angular/core';
import { JobPost } from '../../jobs/models/job-post';
import { BehaviorSubject, debounceTime, distinctUntilChanged, Observable, of, Subscription, switchMap } from 'rxjs';
import { SearchResult } from '../../shared/models/search-result';
import { JobSummary } from '../../jobs/models/job-summary';
import { JobFilter } from '../../jobs/models/job-filter';
import { DataService } from '../../jobs/data.service';
import { IBookmarkable } from '../../shared/models/bookmarkable';
import { JobPreview } from '../../dashboards/models/job-preview';
import { AuthenticationResult } from '../../shared/models/authentication-result';
import { JobBookmark } from '../models/job-bookmark';
import { UiStateStore } from '../../shared/store/ui-state-store.service';
import { IApplicable } from '../../shared/models/applicable';
import { MyJobStatuses } from '../models/job-statuses';
import { JobStatusCount } from '../models/job-status-count';
import { AppStore } from '../../shared/store/lookup-store.service';

@Injectable({
  providedIn: 'root'
})
export class JobsStore {

  public SAVED_JOB_TITLE = "Saved Jobs";
  public PROPOSED_JOB_TITLE = "Proposed Jobs";

  _user: AuthenticationResult;
  _jobFilterStoreageKey = "jobFilter";

  _jobPosts: BehaviorSubject<JobPost[]> = new BehaviorSubject([]);
  public readonly jobPosts: Observable<JobPost[]> = this._jobPosts.asObservable();

  _jobSearchFilter: BehaviorSubject<JobFilter> = new BehaviorSubject(null);
  public readonly jobSearchFilter: Observable<JobFilter> = this._jobSearchFilter.asObservable();

  _jobSearchResult: BehaviorSubject<SearchResult<JobSummary>> = new BehaviorSubject(null);
  public readonly jobSearchResults: Observable<SearchResult<JobSummary>> = this._jobSearchResult.asObservable();

  _jobPreview: BehaviorSubject<JobPreview> = new BehaviorSubject(null);
  public readonly jobPreview: Observable<JobPreview> = this._jobPreview.asObservable();

  _jobStatusCounts: BehaviorSubject<JobStatusCount> = new BehaviorSubject(null);
  public readonly jobStatusCounts: Observable<JobStatusCount> = this._jobStatusCounts.asObservable();

  _latestJobs: BehaviorSubject<JobSummary[]> = new BehaviorSubject(null);
  public readonly latestJobs: Observable<JobSummary[]> = this._latestJobs.asObservable();

  constructor(private dataService: DataService, private uiState: UiStateStore, private lookupStore: AppStore) {
    uiState.authenticatedUser.subscribe(user => {
      this._user = user;
    });

    let jobFilter = new JobFilter();
    this._jobSearchFilter.next(jobFilter);
  }

  public setJobPosts(jobPosts: JobPost[]): void {
    this._jobPosts.next(jobPosts);
  }

  public loadSearchResults(jobFilter: JobFilter): void {
    jobFilter.updateLocationFilter(this.lookupStore);
    this.saveFilter(jobFilter);
    this.jobSearchFilter.pipe(debounceTime(200), distinctUntilChanged(), switchMap(jb => {
      return this.dataService.filterJobs(jb);
    })).subscribe(result => {
      if (!result) {
        return;
      }

      let bookmarksPromise = this.setJobBookmarks(result.result);
      let applicationStatusPromise = this.setJobAppliedStatus(result.result);

      Promise.all([bookmarksPromise, applicationStatusPromise]).then(data => {
        this._jobSearchResult.next(result);
      });
    });

    this._jobSearchFilter.next(jobFilter);
  }

  public bookmarkJobInLocalStore(jobId: string): void {
    let bookmarkedJobs = localStorage.getItem("bookmarkedJobs");
    if (!bookmarkedJobs) {
      localStorage.setItem("bookmarkedJobs", JSON.stringify([""]));
    }

    let bookmarkArray = JSON.parse(localStorage.getItem("bookmarkedJobs")) as string[];
    bookmarkArray.push(jobId);
    localStorage.setItem("bookmarkedJobs", JSON.stringify(bookmarkArray));

    if (!this._user) {
      return;
    }

    let bookmark = new JobBookmark();
    bookmark.jobId = jobId;
    bookmark.accountId = this._user.accountId;
    this.dataService.bookmarkJob(bookmark).subscribe(r => {
      this.getMyJobsStatusCount();
    });
  }

  public unBookmarkJobInLocalStore(jobId: string): void {
    let bookmarkArray = JSON.parse(localStorage.getItem("bookmarkedJobs")) as string[];
    bookmarkArray = bookmarkArray.filter(b => b != jobId);
    localStorage.setItem("bookmarkedJobs", JSON.stringify(bookmarkArray));

    if (!this._user) {
      return;
    }

    let bookmark = new JobBookmark();
    bookmark.jobId = jobId;
    bookmark.accountId = this._user.accountId;
    this.dataService.unBookmarkJob(bookmark).subscribe(r => {
      this.getMyJobsStatusCount();
    });
  }

  public getJobPreview(jobId: string): void {
    let accountId = this._user ? this._user.accountId : null;
    this.dataService.getJobPreview(accountId, jobId).subscribe(job => {
      let jobs: JobPreview[] = [job];
      this.setJobBookmarks(jobs);
      this.setJobAppliedStatus(jobs);

      this._jobPreview.next(job);
    });
  }

  public getMyJobs(status: MyJobStatuses, pageNumber: number): Promise<SearchResult<JobSummary>> {
    let promise = new Promise<SearchResult<JobSummary>>((resolve, reject) => {
      this.dataService.getMyJobs(status, this._user.accountId, pageNumber).subscribe(result => {
        resolve(result);
      }, err => {
        reject();
      });
    });

    return promise;
  }

  public getMyJobIds(status: MyJobStatuses): Promise<string[]> {
    let promise = new Promise<string[]>((resolve, reject) => {
      this.dataService.getMyJobIds(status, this._user.accountId).subscribe(result => {
        resolve(result);
      }, err => {
        reject();
      });
    });

    return promise;
  }

  public getMyJobsStatusCount(): void {
    this.dataService.getMyJobsStatusCount(this._user.accountId).subscribe(count => {
      this._jobStatusCounts.next(count);
    });
  }

  setJobBookmarks(jobs: IBookmarkable[]): Promise<void> | null {
    let jobBookmarks = this.getLocalStoreBookmarkedJobs();
    jobBookmarks.forEach(jobId => {
      let job = jobs.find(r => r.id == jobId);
      if (job) {
        job.bookmarked = true;
      }
    });

    if (!this._user) {
      return null;
    }

    let promise = this.dataService.getJobBookmarks(this._user.accountId)
      .toPromise()
      .then(bookmarks => {
        bookmarks.forEach(bookmark => {
          let job = jobs.find(r => r.id == bookmark.jobId);
          if (job) {
            job.bookmarked = true;
          }
        });
      });

    return promise;
  }

  setJobAppliedStatus(jobs: IApplicable[]): Promise<void> {
    if (!this._user) {
      return;
    }

    let promise = this.dataService.getAppliedJobs(this._user.accountId)
      .toPromise()
      .then(appliedJobs => appliedJobs.forEach(appliedJob => {
        let job = jobs.find(job => job.id == appliedJob.jobId);
        if (job) {
          job.applied = true;
          job.applicationDate = appliedJob.applicationDate;
        }
      }));

    return promise;
  }

  getLocalStoreBookmarkedJobs(): string[] {
    let bookmarkedJobs = localStorage.getItem("bookmarkedJobs");
    if (!bookmarkedJobs) {
      return [];
    }

    return JSON.parse(bookmarkedJobs);
  }

  saveFilter(jobFilter: JobFilter): void {
    localStorage.setItem(this._jobFilterStoreageKey, JSON.stringify(jobFilter));
  }

  getFilter(): JobFilter {
    let jsonString = localStorage.getItem(this._jobFilterStoreageKey);
    let jobFilter = new JobFilter();
    Object.assign(jobFilter, JSON.parse(jsonString));

    return jobFilter;
  }

  getLatestJobs(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.dataService.getLatestJobs().subscribe(result => {
        this._latestJobs.next(result);
        resolve();
      }, err => reject(err));
    });
  }

  declineJobInvite(jobId: string, candidateAccountId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.dataService.declineJobInvite(jobId, candidateAccountId).subscribe(r => {
        resolve();
      }, err => reject(err));
    });
  }

}
